import { useLocation, useNavigate } from 'react-router-dom'
import { type ReactNode } from 'react'
import { ShieldOff, Home } from 'lucide-react'
import { Button, EmptyState, Skeleton } from '@/shared/ui'
import { useCurrentRole } from '../hooks/use-current-role'
import type { RoleTier } from '../domain/role-gating'
import type { ClubPermission } from '@/core/models/staff'
import { useIsPlatformAdmin } from '@/core/auth/use-is-admin'
import { usePreviewSession } from '@/features/preview-session/hooks/use-preview-session'

// Re-exported so route files (which are not allowed to import feature-domain)
// can still reference the tier type without widening boundary rules.
export type { RoleTier }

/** Human-readable labels for each route path — used in the access-denied toast. */
const ROUTE_LABELS: Record<string, string> = {
  '/courts': 'Pistas',
  '/staff': 'Personal',
  '/ingresos': 'Ingresos',
  '/analytics': 'Rendimiento',
  '/settings': 'Club',
  '/series': 'Recurrentes',
  '/ventas': 'Ventas',
  '/catalogo': 'Catálogo',
  '/members': 'Clientes',
  '/calendar': 'Calendario',
  '/activity': 'Actividad',
}

/** Spanish labels for each tier — shown in the access-denied toast. */
const TIER_LABELS: Record<RoleTier, string> = {
  owner: 'Propietario',
  manager: 'Gerente',
  staff: 'Recepción',
}

interface Props {
  requiredRole: RoleTier
  children: ReactNode
}

/**
 * Wraps a route element to enforce a minimum-role-tier guard on top of
 * authentication. Assumes it's rendered inside a `<ProtectedRoute>` — auth
 * status is already `authenticated`.
 *
 * Three bypasses of the tier check:
 *   1. Platform admins (email on `platform_config/admins`) — they impersonate
 *      clubs for support/debug and always need every route.
 *   2. Preview-token sessions (signed in via `redeem_preview_token`) — they
 *      see every screen so prospects can evaluate the product. Writes are
 *      blocked at the Firestore-rules layer (`hasPreviewClaim()` denial),
 *      via the `body.preview-mode` CSS gate on primary/danger buttons,
 *      and via the capture-phase form-submit interceptor in
 *      `usePreviewSession`.
 *   3. Loading state — render nothing until role + admin lookups resolve, to
 *      avoid a screen flash before the gate has the data it needs.
 *
 * When access is denied the gate renders an inline "Sin permiso" screen
 * with a clear message + a way back to a permitted route, instead of
 * silently redirecting the user away from the URL they requested.
 */
export function RoleGate({ requiredRole, children }: Props) {
  const role = useCurrentRole()
  const { isAdmin, isLoading: adminLoading } = useIsPlatformAdmin()
  const preview = usePreviewSession()
  const location = useLocation()
  const navigate = useNavigate()

  const denied =
    !role.isLoading &&
    !adminLoading &&
    !preview.isLoading &&
    !isAdmin &&
    !preview.active &&
    !role.meets(requiredRole)

  if (role.isLoading || adminLoading || preview.isLoading) {
    // Don't render `null` — that paints a blank black page on slow
    // role/admin lookups (staff-walk audit 2026-05-25 caught this on
    // /analytics for a receptionist). A short Skeleton page signals
    // "loading" instead of "broken".
    return (
      <div className="page" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--gap)' }}>
        <Skeleton height={32} width="40%" />
        <Skeleton height={180} />
        <Skeleton height={180} />
      </div>
    )
  }
  if (isAdmin) return <>{children}</>
  if (preview.active) return <>{children}</>
  if (denied) {
    const screenLabel = ROUTE_LABELS[location.pathname] ?? location.pathname
    const roleLabel = role.tier ? TIER_LABELS[role.tier] : 'Sin rol'
    return (
      <div className="page page--center">
        <EmptyState
          icon={ShieldOff}
          title={`Sin permiso para «${screenLabel}»`}
          description={`Tu rol actual (${roleLabel}) no tiene acceso a esta sección. Pide al propietario del club que actualice tus permisos si necesitas entrar.`}
          action={
            <Button variant="primary" icon={Home} onClick={() => navigate('/')}>
              Volver al inicio
            </Button>
          }
        />
      </div>
    )
  }
  return <>{children}</>
}

interface PermissionGateProps {
  requiredPermission: ClubPermission
  children: ReactNode
}

/**
 * Permission-driven sibling of `RoleGate` — used for routes that should be
 * configurable via the role editor instead of tier-pinned (e.g. `/ingresos`,
 * `/ventas`, `/catalogo`). Same bypass list (platform admin + preview), same
 * loading-skeleton + Sin-permiso fallback.
 */
export function PermissionGate({ requiredPermission, children }: PermissionGateProps) {
  const role = useCurrentRole()
  const { isAdmin, isLoading: adminLoading } = useIsPlatformAdmin()
  const preview = usePreviewSession()
  const location = useLocation()
  const navigate = useNavigate()

  const denied =
    !role.isLoading &&
    !adminLoading &&
    !preview.isLoading &&
    !isAdmin &&
    !preview.active &&
    !role.can(requiredPermission)

  if (role.isLoading || adminLoading || preview.isLoading) {
    return (
      <div className="page" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--gap)' }}>
        <Skeleton height={32} width="40%" />
        <Skeleton height={180} />
        <Skeleton height={180} />
      </div>
    )
  }
  if (isAdmin) return <>{children}</>
  if (preview.active) return <>{children}</>
  if (denied) {
    const screenLabel = ROUTE_LABELS[location.pathname] ?? location.pathname
    return (
      <div className="page page--center">
        <EmptyState
          icon={ShieldOff}
          title={`Sin permiso para «${screenLabel}»`}
          description={`Tu rol actual no incluye el permiso necesario para esta sección. Pide al propietario del club que te lo asigne en Personal → Permisos.`}
          action={
            <Button variant="primary" icon={Home} onClick={() => navigate('/')}>
              Volver al inicio
            </Button>
          }
        />
      </div>
    )
  }
  return <>{children}</>
}
